import React, { useState } from 'react'
import { Question } from '../types'
import NPSQuestion from './questions/NPSQuestion'
import MultipleOptionQuestion from './questions/MultipleOptionQuestion'
import OpenEndedQuestion from './questions/OpenEndedQuestion'
import DeleteIcon from '@/icons/delete.svg'
import ArrowUpIcon from '@/icons/arrow_up.svg'
import ArrowDownIcon from '@/icons/arrow_down.svg'

interface QuestionFormProps {
  question: Omit<Question, 'id'> & { id?: number }
  onQuestionChange: (question: Partial<Omit<Question, 'id'> & { id?: number }>) => void
  onDelete: () => void
  questionNumber: number
  onMoveUp: () => void
  onMoveDown: () => void
  isFirst: boolean
  isLast: boolean
  isAccessibilitySurvey?: boolean
  onCsatChange?: (questionId: number | undefined, isCsat: boolean) => void
  hasOtherCsatQuestion?: boolean
}

const QuestionForm: React.FC<QuestionFormProps> = ({
  question,
  onQuestionChange,
  onDelete,
  questionNumber,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  isAccessibilitySurvey = false,
  onCsatChange,
  hasOtherCsatQuestion = false
}) => {
  const [showPreview, setShowPreview] = useState(false)

  const handleChange = (field: keyof Question, value: any) => {
    onQuestionChange({ [field]: value })
  }

  const handlePayloadChange = (payload: any) => {
    onQuestionChange({ payload })
  }

  const addOption = () => {
    const currentOptions = question.payload?.options || []
    const newPosition = currentOptions.length + 1
    const newOptions = [...currentOptions, { text: '', position: newPosition }]
    handlePayloadChange({ ...question.payload, options: newOptions })
  }

  const updateOption = (index: number, text: string) => {
    const newOptions = [...(question.payload?.options || [])]
    if (newOptions[index]) {
      newOptions[index] = { 
        ...newOptions[index],
        text,
        position: index + 1 
      }
      handlePayloadChange({ ...question.payload, options: newOptions })
    }
  }

  const removeOption = (index: number) => {
    if (question.payload?.options && question.payload.options.length > 0) {
      const newOptions = [...question.payload.options]
      newOptions.splice(index, 1)
      handlePayloadChange({ ...question.payload, options: newOptions })
    }
  }

  return (
    <div className="flex gap-2 items-start">
      <div className="flex-grow bg-blue-100 rounded-lg pt-6 mt-4 border border-gray-200 relative">
        {!showPreview ? (
          <div>
            <div className="space-y-6 px-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Pregunta {questionNumber}</h3>
              </div>
              <div className="field">
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de pregunta</label>
                <select
                  value={question.kind}
                  onChange={e => handleChange('kind', e.target.value as Question['kind'])}
                  className="w-72 p-2 border-2 border-black rounded-lg bg-white"
                  required
                >
                  <option value="nps">NPS</option>
                  <option value="multiple_option">Opción múltiple - radio button</option>
                  <option value="open_ended">Campo abierto</option>
                </select>
              </div>
              <div className="field">
                <label className="block text-sm font-medium text-gray-700 mb-2">Enunciado de la pregunta</label>
                <textarea
                  value={question.body}
                  onChange={e => handleChange('body', e.target.value)}
                  className="w-full p-2 border-2 border-black rounded-lg h-24 bg-white"
                  placeholder="Escribe aquí tu pregunta"
                  required
                />
              </div>
              {question.kind === 'multiple_option' && (
                <div className="field">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Alternativas de respuesta</label>
                  <MultipleOptionQuestion
                    questionId={question.id || -1}
                    body=""
                    required={false}
                    options={question.payload?.options || []}
                    onAddOption={addOption}
                    onRemoveOption={removeOption}
                    onUpdateOption={updateOption}
                    readOnly={false}
                  />
                </div>
              )}
            </div>
            <div className="flex items-center justify-between mt-4 bg-blue-200 px-6 py-3">
              <div className="flex items-center space-x-6">
                <div className="flex items-center px-3 py-2 rounded-lg">
                  <button
                    type="button"
                    onClick={() => handleChange('required', !question.required)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
                      question.required ? 'bg-blue-700' : 'bg-gray-200'
                    }`}
                    role="switch"
                    aria-checked={question.required}
                  >
                    <span
                      className={`inline-block h-5 w-5 rounded-full bg-white transform transition-transform ${
                        question.required ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="ml-3 text-sm font-medium text-gray-700">Pregunta obligatoria</span>
                </div>
                {isAccessibilitySurvey && question.kind === 'multiple_option' && (
                  <div className="flex items-center px-3 py-2 rounded-lg">
                    <button
                      type="button"
                      onClick={() => {
                        const newCsatValue = !question.is_csat;
                        handleChange('is_csat', newCsatValue);
                        if (onCsatChange) {
                          onCsatChange(question.id, newCsatValue);
                        }
                      }}
                      disabled={!question.is_csat && hasOtherCsatQuestion}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
                        question.is_csat ? 'bg-blue-700' : (!question.is_csat && hasOtherCsatQuestion) ? 'bg-gray-300' : 'bg-gray-200'
                      } ${(!question.is_csat && hasOtherCsatQuestion) ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      role="switch"
                      aria-checked={question.is_csat}
                    >
                      <span
                        className={`inline-block h-5 w-5 rounded-full bg-white transform transition-transform ${
                          question.is_csat ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className={`ml-3 text-sm font-medium ${(!question.is_csat && hasOtherCsatQuestion) ? 'text-gray-400' : 'text-gray-700'}`}>
                      Para medición de satisfacción de accesibilidad
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-blue-700 text-sm font-medium hover:text-blue-900 cursor-pointer"
                >
                  Vista previa
                </button>
                <button
                  type="button"
                  onClick={onDelete}
                  className="text-gray-600 hover:text-gray-800"
                  title="Delete question"
                >
                  <img src={DeleteIcon} alt="Delete" className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-4">
            <div className="bg-white p-5 border border-gray-200 rounded-lg mb-4 mx-6">
              {question.kind === 'nps' && (
                <NPSQuestion body={question.body} required={question.required} readOnly={true} />
              )}
              {question.kind === 'multiple_option' && (
                <MultipleOptionQuestion
                  questionId={question.id || -1}
                  body={question.body}
                  required={question.required}
                  options={question.payload?.options || []}
                  readOnly={true}
                />
              )}
              {question.kind === 'open_ended' && (
                <OpenEndedQuestion body={question.body} required={question.required} readOnly={true} />
              )}
            </div>
            <div className="flex justify-end mr-6">
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Volver a editar
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col items-start gap-2 mt-4 w-32">
        {!isLast && (
          <button
            type="button"
            onClick={onMoveDown}
            className="flex items-center px-2 py-1 text-blue-600 hover:text-blue-800"
            title="Bajar pregunta"
          >
            <img src={ArrowDownIcon} alt="Down" className="h-10 w-10 mr-1" />
            <span className="text-sm">Bajar pregunta</span>
          </button>
        )}
        {!isFirst && !isLast && <hr className="w-full border-t border-gray-200 my-2" />}
        {!isFirst && (
          <button
            type="button"
            onClick={onMoveUp}
            className="flex items-center px-2 py-1 text-blue-600 hover:text-blue-800"
            title="Subir pregunta"
          >
            <img src={ArrowUpIcon} alt="Up" className="h-10 w-10 mr-1" />
            <span className="text-sm">Subir pregunta</span>
          </button>
        )}
      </div>
    </div>
  )
}

export default QuestionForm
